import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Copy, ExternalLink, Monitor, Tv } from "lucide-react";
import { toast } from "sonner";
import { generateMenuBoard } from "@beztack/menu-board";
import type { MenuBoardLayout, MenuInput } from "@beztack/menu-board";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useActiveOrganization } from "@/hooks/use-organizations";
import { useMenuProducts, useMenuSections, useMenuCollections } from "@/hooks/use-menu";

const UPLOADTHING_BASE_URL = "https://paj514wbqe.ufs.sh/f";

function buildMenuInput(
  sections: { id: string; name: string }[],
  products: {
    id: string;
    name: string;
    price: number;
    sectionId: string | null;
    imageKey: string | null;
  }[],
  collections: { id: string; name: string }[],
): MenuInput {
  const categories = sections.map((s) => ({ id: s.id, name: s.name }));

  const menuProducts = products
    .filter((p) => p.sectionId)
    .map((p) => ({
      id: p.id,
      name: p.name,
      price: p.price,
      categoryId: p.sectionId as string,
      image: p.imageKey ? `${UPLOADTHING_BASE_URL}/${p.imageKey}` : undefined,
    }));

  const combos = collections.map((c) => ({
    id: c.id,
    name: c.name,
    price: 0,
    popularity: 80,
  }));

  return {
    categories,
    products: menuProducts,
    combos: combos.length > 0 ? combos : undefined,
  };
}

function getScreenUrl(orgSlug: string, screenIndex: number): string {
  return `${window.location.origin}/b/${orgSlug}/${String(screenIndex)}`;
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success("URL copied to clipboard"),
    () => toast.error("Failed to copy URL"),
  );
}

function ScreenPreview({
  screen,
  orgSlug,
}: {
  screen: MenuBoardLayout["screens"][number];
  orgSlug: string;
}) {
  const { t } = useTranslation();
  const url = getScreenUrl(orgSlug, screen.index);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Monitor className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">
              {t("menu.board.screen", { defaultValue: "Screen" })} {String(screen.index + 1)}
            </CardTitle>
          </div>
          <Badge variant="outline">
            {String(screen.itemCount)} {t("menu.board.items", { defaultValue: "items" })}
          </Badge>
        </div>
        <CardDescription className="font-mono text-xs">{url}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {screen.sections.map((section, i) => (
            <div
              className={`rounded-lg border p-3 ${
                section.layout.emphasis === "high"
                  ? "border-primary/50 bg-primary/5"
                  : section.layout.emphasis === "medium"
                    ? "border-border bg-muted/50"
                    : "border-border/50 bg-background"
              }`}
              key={`${section.type}-${String(i)}`}
            >
              <div className="mb-2 flex items-center gap-2">
                <Badge variant={section.layout.emphasis === "high" ? "default" : "secondary"}>
                  {section.type}
                </Badge>
                <span className="text-muted-foreground text-xs">
                  {String(section.items.length)} {t("menu.board.items", { defaultValue: "items" })}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {section.items.map((item) => (
                  <Badge key={item.id} variant="outline">
                    {item.id}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <Button onClick={() => copyToClipboard(url)} size="sm" variant="outline">
            <Copy className="mr-1.5 h-3.5 w-3.5" />
            {t("menu.board.copyUrl", { defaultValue: "Copy URL" })}
          </Button>
          <Button asChild size="sm" variant="outline">
            <a href={url} rel="noopener" target="_blank">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              {t("menu.board.openFullscreen", {
                defaultValue: "Open Fullscreen",
              })}
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MenuBoardPage() {
  const { t } = useTranslation();
  const { data: activeOrg } = useActiveOrganization();
  const { data: sectionsData, isLoading: sectionsLoading } = useMenuSections(activeOrg?.id);
  const { data: productsData, isLoading: productsLoading } = useMenuProducts(activeOrg?.id);
  const { data: collectionsData, isLoading: collectionsLoading } = useMenuCollections(
    activeOrg?.id,
  );

  const isLoading = sectionsLoading || productsLoading || collectionsLoading;

  const layout = useMemo(() => {
    if (!sectionsData?.sections || !productsData?.products || !collectionsData?.collections) {
      return null;
    }

    const input = buildMenuInput(
      sectionsData.sections,
      productsData.products,
      collectionsData.collections,
    );

    if (input.products.length === 0) return null;

    return generateMenuBoard(input);
  }, [sectionsData, productsData, collectionsData]);

  const orgSlug = activeOrg?.slug ?? "";

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 px-4 py-6 lg:px-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!layout || layout.screens.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 px-4 py-20 lg:px-6">
        <Tv className="h-12 w-12 text-muted-foreground/40" />
        <h2 className="font-semibold text-xl">
          {t("menu.board.emptyTitle", {
            defaultValue: "No board screens available",
          })}
        </h2>
        <p className="max-w-md text-center text-muted-foreground text-sm">
          {t("menu.board.emptyDescription", {
            defaultValue: "Add products to your menu sections to generate board screens.",
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 px-4 py-6 lg:px-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-semibold text-2xl tracking-tight">
            {t("menu.board.title", { defaultValue: "Menu Board" })}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("menu.board.subtitle", {
              defaultValue: "Generate fullscreen URLs for your digital menu displays.",
            })}
          </p>
        </div>
        <Badge variant="secondary">
          {String(layout.screens.length)} {t("menu.board.screens", { defaultValue: "screens" })}{" "}
          &middot; {layout.layoutType}
        </Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {layout.screens.map((screen) => (
          <ScreenPreview key={screen.index} orgSlug={orgSlug} screen={screen} />
        ))}
      </div>
    </div>
  );
}
